import { FC, Fragment } from 'react';
import { useFormContext } from 'react-hook-form';
// Redux
import { useAppSelector } from 'app/hooks/useStore';
// Types
import { StepTasks, StepTaskLabels } from 'app/store/AutomatedWorkflows/AutomatedWorkflows.types';
// Models
import { RootState } from 'app/store';
import { IWorkflowStep } from 'app/store/AutomatedWorkflows/AutomatedWorkflows.models';
// Selectors
import { selectPromptById } from 'app/store/AIChatPrompts/AIChatPrompts.selectors';
// Mui
import { Theme, Box, Typography, Tooltip } from '@mui/material';
// Utilities
import { isRequired, isMin, isMax, isEmailValid, isMaxLength } from 'app/utilities/Validations';
// 
import { useWorkflowContext } from './Workflow.context';

type Props = {
  step: IWorkflowStep;
}

const StepBlock:FC<Props> = ({
  // Props
  step
}) => {
  const { register, formState:{ errors }, watch } = useFormContext();
  const { selectedStep, onSelectStep, getStepFieldIndex } = useWorkflowContext();

  const fieldIndex = getStepFieldIndex(step.id);

  const parameters = watch(`steps[${fieldIndex}].input.parameters`);
  const promptId = parameters?.promptId;

  const prompt = useAppSelector((state:RootState) => {
    if (step.task !== StepTasks.RunPrompt || !promptId) return undefined;
    return selectPromptById(state, { promptId });
  });

  const handleClick = () => {
    onSelectStep(step);
  }

  const subtitles:Record<StepTasks, string> = {
    [StepTasks.RunPrompt]: promptId && prompt ? prompt.title : '',
    [StepTasks.CreateDocument]: parameters?.name ?? '',
    [StepTasks.CreateReport]: parameters?.name ?? '',
    [StepTasks.SendEmail]: parameters?.to ?? ''
  }

  // As on submit validation of hidden field not work
  // we should add hidden input with validation rules
  const renderInputsWithRules = () => {
    switch (step.task) {
      case StepTasks.RunPrompt:
        return (
          <Fragment>
            <input {...register(`steps[${fieldIndex}].input.parameters.promptId`, {
              // We used `isRequired` not `true` to throw an message
              required: isRequired
            })} type="hidden" />
            <input {...register(`steps[${fieldIndex}].input.parameters.coverage`, {
              min: isMin(0),
              max: isMax(100)
            })} type="hidden" />
          </Fragment>
        );
      case StepTasks.CreateDocument:
        return (
          <input {...register(`steps[${fieldIndex}].input.parameters.collectionId`, {
            required: isRequired
          })} type="hidden" />
        );
      case StepTasks.CreateReport:
        return (
          <input {...register(`steps[${fieldIndex}].input.parameters.watermark.text`, {
            maxLength: isMaxLength(21)
          })} type="hidden" />
        );
      case StepTasks.SendEmail:
        return (
          <input {...register(`steps[${fieldIndex}].input.parameters.to`, {
            required: isRequired,
            pattern: isEmailValid

          })} type="hidden" />
        );
      default:
        break;
    }
  };

  const isStepSelected = selectedStep && selectedStep.id === step.id;
  const isStepInvalid = errors.steps && (errors.steps as any)[fieldIndex];

  return (
    <Fragment>
      {renderInputsWithRules()}
      <Box
        sx={{
          width: '100%',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'white',
          borderRadius: 1,
          p: 4,
          '&:hover': {
            bgcolor: (theme:Theme) => theme.palette.grey[100],
            cursor: 'pointer'
          },
          '&::before': {
            content: '""',
            borderRadius: 1,
            border: (theme:Theme) => `2px solid ${
              isStepSelected
              ? theme.palette.primary.main
              : isStepInvalid
                ? theme.palette.error.main
                : 'transparent'
            }`,
            boxShadow: (theme:Theme) => `0 0 4px ${
              isStepSelected
              ? theme.palette.primary.main
              : isStepInvalid
                ? theme.palette.error.main
                : 'transparent'
            }`,
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            pointerEvents: 'none'
          }
        }}
        onClick={handleClick}
      >
        <Typography variant="body1">{StepTaskLabels[step.task]}</Typography>
        <Tooltip title={subtitles[step.task]} placement="bottom" arrow>
          <Typography sx={{
            maxWidth: '100%', fontSize: 11, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden'
          }}>{subtitles[step.task]}</Typography>
        </Tooltip>
      </Box>
    </Fragment>
  )
}

export default StepBlock;
